// Backup.jsx
// ✅ NO hooks (class component)
// ✅ 2 tabs:
//    1) Download: choose entity -> start export job (202)
//    2) Backups: auto-load and show ALL ready .csv backups -> Download button per row
//
// ✅ English-only UI/messages
// ✅ Backups tab auto-fetches when opened (also has Refresh button)
// ✅ Download button downloads CSV immediately (no extra steps)
//
// Backend endpoints used:
// - Start export:  GET /exports/:entity
// - List backups:  GET /exports/backups   (recommended)
//   Fallback:      GET /exports/backups?entity=words  (loops entities client-side)
// - Download:      uses file.downloadUrl if provided
//   else           GET /exports/backups/:entity/:fileName

import React from "react";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import CustomTabs from "components/CustomTabs/CustomTabs.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

import Book from "@material-ui/icons/Book";
import BackupIcon from "@material-ui/icons/Backup";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import RefreshIcon from "@material-ui/icons/Refresh";
import { BeatLoader } from "react-spinners";

function ensureApiBase(url) {
  const s = String(url || "").trim();
  if (!s) return "";
  return s.endsWith("/") ? s : s + "/";
}

function getFilenameFromContentDisposition(contentDisposition) {
  if (!contentDisposition) return null;
  const match =
    /filename\*?=(?:UTF-8''|")?([^;"\n]+)"?/i.exec(contentDisposition);
  if (!match || !match[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

function safeJoinUrl(apiBase, maybePath) {
  const base = ensureApiBase(apiBase);
  const p = String(maybePath || "").trim();
  if (!p) return "";
  return base + p.replace(/^\//, "");
}

// ------------------------------
// Backups Tab Content (auto-load)
// ------------------------------
class BackupsTabContent extends React.Component {
  componentDidMount() {
    // Auto-fetch backups when the Backups tab is opened/mounted
    if (typeof this.props.onAutoLoad === "function") {
      this.props.onAutoLoad();
    }
  }

  render() {
    const {
      backups,
      isRefreshingBackups,
      search,
      onSearchChange,
      onRefresh,
      onDownload,
      downloadingKey,
    } = this.props;

    const q = String(search || "").toLowerCase().trim();
    const filtered = !q
      ? backups
      : backups.filter((f) => {
          const fileName = String(f?.fileName || "").toLowerCase();
          const folder = String(f?.folder || "").toLowerCase();
          const entity = String(f?.entity || "").toLowerCase();
          return fileName.includes(q) || folder.includes(q) || entity.includes(q);
        });

    return (
      <Card style={{ maxWidth: 1100, margin: "3rem auto" }}>
        <CardHeader
          color="primary"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            minHeight: 64,
            padding: "16px",
          }}
        >
          <span style={{ width: "100%", fontWeight: 700 }}>
            Backups (Ready to Download)
          </span>
        </CardHeader>

        <CardBody>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <Button
                color="primary"
                onClick={onRefresh}
                disabled={isRefreshingBackups}
                style={{
                  minWidth: 170,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  color:"white"
                }}
              >
                <RefreshIcon style={{ fontSize: 18 }} />
                {isRefreshingBackups ? "Refreshing..." : "Refresh list"}
              </Button>

              {isRefreshingBackups && (
                <BeatLoader color={"rgba(0, 0, 0, 0.85)"} size={10} />
              )}
            </div>

            <div style={{ width: 360, maxWidth: "100%" }}>
              <input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search by entity / file / folder..."
                style={{
                  width: "90%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(0,0,0,0.2)",
                  outline: "none",
                }}
              />
            </div>
          </div>

          <div
            style={{
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.1fr 2.6fr 1.3fr 1fr 1.2fr",
                padding: "12px 12px",
                background: "rgba(0,0,0,0.03)",
                fontWeight: 700,
                fontSize: 13,
                gap: 10,
              }}
            >
              <div>Entity</div>
              <div>File</div>
              <div>Created</div>
              <div>Size</div>
              <div style={{ textAlign: "right" }}>Download</div>
            </div>

            {!filtered.length ? (
              <div style={{ padding: 14, textAlign: "center", opacity: 0.7 }}>
                No backups available yet. Click “Refresh list” or generate from the Download tab.
              </div>
            ) : (
              filtered.map((f) => {
                const entity = String(f.entity || "").toLowerCase();
                const fileName = f.fileName;
                const key = `${entity}::${fileName}`;
                const isDownloading = downloadingKey === key;

                return (
                  <div
                    key={`${entity}-${f.folder}-${fileName}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.1fr 2.6fr 1.3fr 1fr 1.2fr",
                      padding: "12px 12px",
                      borderTop: "1px solid rgba(0,0,0,0.08)",
                      alignItems: "center",
                      fontSize: 13,
                      gap: 10,
                    }}
                  >
                    <div style={{ fontWeight: 700, textTransform: "lowercase" }}>
                      {entity || "—"}
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={fileName}
                      >
                        {fileName}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        Folder: {f.folder || "—"}
                      </div>
                    </div>

                    <div style={{ opacity: 0.85 }}>{formatDate(f.createdAt)}</div>
                    <div style={{ opacity: 0.85 }}>{formatBytes(f.size)}</div>

                    <div style={{ textAlign: "right" }}>
                      <Button
                        color="primary"
                        onClick={() => onDownload(f)}
                        disabled={isDownloading}
                        style={{
                          minWidth: 140,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          color:"white"
                        }}
                      >
                        <CloudDownloadIcon style={{ fontSize: 18 }} />
                        {isDownloading ? "Downloading..." : "Download"}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div
            style={{
              textAlign: "center",
              fontSize: 12,
              opacity: 0.7,
              marginTop: 12,
            }}
          >
            Only completed <b>.csv</b> files are shown here.
          </div>
        </CardBody>
      </Card>
    );
  }
}

export default class Backup extends React.Component {
  constructor(props) {
    super(props);

    this.apiBase = ensureApiBase(process.env.REACT_APP_API_URL);

    this.exportOptions = [
      { value: "words", label: "Words (words)" },
      { value: "roots", label: "Roots (roots)" },
      { value: "word_lists", label: "Word Lists (word_lists)" },
      { value: "word_list_entries", label: "Word List Entries (word_list_entries)" },
      { value: "word_roots_map", label: "Word Roots Map (word_roots_map)" },
    ];

    this.state = {
      // Download tab
      selectedExport: null,
      isStarting: false,

      // Backups tab
      backups: [],
      isRefreshingBackups: false,
      downloadingKey: null,
      search: "",
      hasLoadedBackupsOnce: false,
    };

    this.handleStartExportFromDownloadTab =
      this.handleStartExportFromDownloadTab.bind(this);

    this.refreshAllBackups = this.refreshAllBackups.bind(this);
    this.downloadRow = this.downloadRow.bind(this);
  }

  // ---------------------
  // Auth helpers
  // ---------------------
  getCurrentUser() {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  }

  assertAuthOrRedirect() {
    const currentUser = this.getCurrentUser();

    if (!currentUser) {
      toast.error("Unauthorized. Please log in.", {
        position: toast.POSITION.TOP_CENTER,
      });
      window.location.href = "/";
      return null;
    }

    if (new Date() > new Date(currentUser.expiresInTime)) {
      toast.error("Session expired. Please log in again.", {
        position: toast.POSITION.TOP_CENTER,
      });
      localStorage.removeItem("user");
      window.location.href = "/";
      return null;
    }

    if (currentUser.role !== "Super Admin") {
      toast.error("Unauthorized. Only Super Admin has access.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return null;
    }

    return currentUser;
  }

  // ---------------------
  // API urls
  // ---------------------
  buildStartExportUrl(entity) {
    return `${this.apiBase}exports/${encodeURIComponent(entity)}`;
  }

  buildListAllBackupsUrl() {
    return `${this.apiBase}exports/backups`;
  }

  buildListByEntityUrl(entity) {
    return `${this.apiBase}exports/backups?entity=${encodeURIComponent(entity)}`;
  }

  // fallback download (if backend doesn’t return downloadUrl)
  buildDownloadUrl(entity, fileName) {
    return `${this.apiBase}download/backups/${encodeURIComponent(
      entity
    )}/${encodeURIComponent(fileName)}`;
  }

  // ---------------------
  // Start export (Download tab)
  // ---------------------
  async handleStartExportFromDownloadTab() {
    if (this.state.isStarting) return;

    const currentUser = this.assertAuthOrRedirect();
    if (!currentUser) return;

    if (!this.state.selectedExport?.value) {
      toast.error("Please select what you want to export.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    this.setState({ isStarting: true });

    try {
      const entity = this.state.selectedExport.value;
      const url = this.buildStartExportUrl(entity);

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + currentUser.accessToken,
        },
      });

      if (res.status !== 202 && !res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Export start failed (${res.status})`);
      }

      await res.json().catch(() => null);

      toast.success("Export started. Open the Backups tab to download when ready.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to start export. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      this.setState({ isStarting: false });
    }
  }

  // ---------------------
  // Backups: refresh ALL .csv
  // ---------------------
  async refreshAllBackups(silent = false) {
    if (this.state.isRefreshingBackups) return;

    const currentUser = this.assertAuthOrRedirect();
    if (!currentUser) return;

    this.setState({ isRefreshingBackups: true });

    try {
      // Try "list all" endpoint first
      let res = await fetch(this.buildListAllBackupsUrl(), {
        method: "GET",
        headers: { Authorization: "Bearer " + currentUser.accessToken },
      });

      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        const list = Array.isArray(json?.files) ? json.files : [];

        // Ensure only .csv (extra safety)
        const cleaned = list.filter((x) =>
          String(x?.fileName || "").toLowerCase().endsWith(".csv")
        );

        this.setState({
          backups: cleaned,
          hasLoadedBackupsOnce: true,
        });

        if (!silent) {
          toast.success("Backups list updated.", {
            position: toast.POSITION.TOP_CENTER,
          });
        }
        return;
      }

      // Fallback: loop per entity if backend requires ?entity=
      const merged = [];
      for (let i = 0; i < this.exportOptions.length; i++) {
        const entity = this.exportOptions[i].value;

        const r = await fetch(this.buildListByEntityUrl(entity), {
          method: "GET",
          headers: { Authorization: "Bearer " + currentUser.accessToken },
        });

        if (!r.ok) continue;

        const j = await r.json().catch(() => ({}));
        const files = Array.isArray(j?.files) ? j.files : [];

        files.forEach((f) => {
          if (String(f?.fileName || "").toLowerCase().endsWith(".csv")) {
            merged.push({ ...f, entity });
          }
        });
      }

      merged.sort((a, b) =>
        String(a.createdAt) < String(b.createdAt) ? 1 : -1
      );

      this.setState({
        backups: merged,
        hasLoadedBackupsOnce: true,
      });

      if (!silent) {
        toast.success("Backups list updated.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (err) {
      console.error(err);
      if (!silent) {
        toast.error("Failed to load backups. Please try again.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } finally {
      this.setState({ isRefreshingBackups: false });
    }
  }

  // ---------------------
  // Download row -> CSV immediately
  // ---------------------
  async downloadRow(fileObj) {
    const currentUser = this.assertAuthOrRedirect();
    if (!currentUser) return;

    const entity = String(fileObj?.entity || "").toLowerCase();
    const fileName = String(fileObj?.fileName || "").trim();

    if (!fileName || !fileName.toLowerCase().endsWith(".csv")) {
      toast.error("Invalid CSV file.", { position: toast.POSITION.TOP_CENTER });
      return;
    }

    const key = `${entity}::${fileName}`;
    if (this.state.downloadingKey) return;

    this.setState({ downloadingKey: key });

    try {
      // Prefer downloadUrl from backend if provided
      const direct = String(fileObj?.downloadUrl || "").trim();
      const url =  this.buildDownloadUrl(entity, fileName);
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + currentUser.accessToken,
        },
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Download failed (${res.status})`);
      }

      const blob = await res.blob();
      const filename =
        getFilenameFromContentDisposition(res.headers.get("content-disposition")) ||
        fileName;

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success("Download started.", { position: toast.POSITION.TOP_CENTER });
    } catch (err) {
      console.error(err);
      toast.error("Download failed. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      this.setState({ downloadingKey: null });
    }
  }

  render() {
    const currentUser = this.getCurrentUser();
    const hasAccess = currentUser?.role === "Super Admin";

    const { selectedExport, isStarting } = this.state;

    return (
      <div>
        <ToastContainer />

        {hasAccess ? (
          <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
              <CustomTabs
                headerColor="primary"
                tabs={[
                  // -------------------
                  // TAB 1: DOWNLOAD
                  // -------------------
                  {
                    tabName: "Download",
                    tabIcon: Book,
                    tabContent: (
                      <Card style={{ maxWidth: 650, margin: "3rem auto" }}>
                        <CardHeader
                          color="primary"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            minHeight: 64,
                            padding: "16px",
                          }}
                        >
                          <span style={{ width: "100%" }}>
                            Download CSV Exports
                          </span>
                        </CardHeader>

                        <CardBody>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 18,
                              padding: "10px 0",
                            }}
                          >
                            <div style={{ width: "100%", maxWidth: 420 }}>
                              <div
                                style={{
                                  textAlign: "center",
                                  marginBottom: 10,
                                  fontWeight: 600,
                                }}
                              >
                                Select what to export
                              </div>

                              <Select
                                value={selectedExport}
                                onChange={(v) =>
                                  this.setState({ selectedExport: v })
                                }
                                options={this.exportOptions}
                                isDisabled={isStarting}
                                placeholder="Choose export..."
                              />
                            </div>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 12,
                                width: "100%",
                              }}
                            >
                              <Button
                                color="primary"
                                onClick={this.handleStartExportFromDownloadTab}
                                disabled={isStarting || !selectedExport}
                                style={{ minWidth: 170 }}
                              >
                                {isStarting ? "Starting..." : "Generate export"}
                              </Button>

                              {isStarting && (
                                <BeatLoader
                                  color={"rgba(0, 0, 0, 0.97)"}
                                  size={12}
                                />
                              )}
                            </div>

                            <div
                              style={{
                                textAlign: "center",
                                fontSize: 12,
                                opacity: 0.7,
                                maxWidth: 520,
                              }}
                            >
                              This will generate a backup file on the server. Open the{" "}
                              <b>Backups</b> tab to download it when ready.
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ),
                  },

                  // -------------------
                  // TAB 2: BACKUPS
                  // -------------------
                  {
                    tabName: "Backups",
                    tabIcon: BackupIcon,
                    tabContent: (
                      <BackupsTabContent
                        backups={this.state.backups}
                        isRefreshingBackups={this.state.isRefreshingBackups}
                        search={this.state.search}
                        downloadingKey={this.state.downloadingKey}
                        onSearchChange={(v) => this.setState({ search: v })}
                        onRefresh={() => this.refreshAllBackups(false)}
                        onAutoLoad={() => {
                          // Load only once automatically (then user can refresh)
                          if (!this.state.hasLoadedBackupsOnce) {
                            this.refreshAllBackups(true);
                          }
                        }}
                        onDownload={this.downloadRow}
                      />
                    ),
                  },
                ]}
              />
            </GridItem>
          </GridContainer>
        ) : (
          <h1 style={{ textAlign: "center" }}>
            Unauthorized page. Please contact admin
          </h1>
        )}
      </div>
    );
  }
}
